import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from "react";
import type { KarelIde } from "./karelIde";
import type { KarelEditor } from "../editor/karelCodeEditor";
import {
  CodeMirrorEditor,
  type CodeMirrorEditorHandle,
} from "../editor/CodeMirrorEditor";
import { useThemeStore } from "../app/stores/themeStore";
import { requireKarelCodeText } from "../util/assets";
import { useKarelIde } from "./useKarelIde";
import styles from "./KarelIdeComponent.module.css";

export interface KarelIdeHandle {
  getEditor(): KarelEditor | null;
  getIde(): KarelIde | null;
  setReadOnly(readOnly: boolean): void;
  setCode(code: string): void;
  animateCode(code: string, callback: () => void): void;
  animateFile(path: string, callback: () => void): void;
  pause(): void;
  step(callback?: (error?: unknown) => void): void;
  isRunning(): boolean;
  setInputs(inputs: string[]): void;
}

interface KarelIdeComponentProps {
  world: string;
  readOnly?: boolean;
  panelHeader?: ReactNode;
  panelOverlay?: ReactNode;
  onOutput?: (lines: string[]) => void;
}

export const KarelIdeComponent = forwardRef<
  KarelIdeHandle,
  KarelIdeComponentProps
>(function KarelIdeComponent(
  { world, readOnly = false, panelHeader, panelOverlay, onOutput },
  ref,
) {
  const editorHandleRef = useRef<CodeMirrorEditorHandle>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasBodyRef = useRef<HTMLDivElement>(null);
  const animatingRef = useRef(false);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamTokenRef = useRef(0);
  const languageMode = useThemeStore((s) => s.languageMode);

  const getEditor = useCallback(
    () => editorHandleRef.current?.getEditor() ?? null,
    [],
  );
  const { ideRef, ready: canvasReady } = useKarelIde(world, {
    canvasRef,
    observe: canvasBodyRef,
    getEditor,
    debounceMs: 100,
    maxDimension: 3000,
    onOutput,
  });

  const cancelStream = (): void => {
    streamTokenRef.current += 1;
    if (animationTimerRef.current !== null) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }
    animatingRef.current = false;
  };

  const streamCode = (
    editor: KarelEditor,
    code: string,
    callback: () => void,
  ): void => {
    cancelStream();
    const token = streamTokenRef.current;
    animatingRef.current = true;
    editor.setReadOnly(true);
    let index = 0;
    const step = (): void => {
      if (token !== streamTokenRef.current) return;
      if (index >= code.length) {
        animatingRef.current = false;
        animationTimerRef.current = null;
        editor.setReadOnly(readOnly);
        callback();
        return;
      }
      while (index < code.length && code.charAt(index) === "\r") index += 1;
      index += 1;
      editor.setValue(code.substring(0, index));
      animationTimerRef.current = setTimeout(step, 45);
    };
    step();
  };

  useImperativeHandle(ref, () => ({
    getEditor: () => editorHandleRef.current?.getEditor() ?? null,
    getIde: () => ideRef.current,
    setReadOnly: (value: boolean) =>
      editorHandleRef.current?.getEditor()?.setReadOnly(value),
    setCode: (code: string) => {
      cancelStream();
      const editor = editorHandleRef.current?.getEditor();
      editor?.setValue(code);
      editor?.setReadOnly(readOnly);
    },
    animateCode: (code: string, callback: () => void) => {
      const editor = editorHandleRef.current?.getEditor();
      if (!editor) {
        callback();
        return;
      }
      streamCode(editor, code, callback);
    },
    animateFile: (path: string, callback: () => void) => {
      const code = requireKarelCodeText(path);
      const editor = editorHandleRef.current?.getEditor();
      if (!editor) {
        callback();
        return;
      }
      streamCode(editor, code, callback);
    },
    pause: () => ideRef.current?.pauseButton(),
    step: (callback?: (error?: unknown) => void) => {
      void ideRef.current?.stepButton(callback);
    },
    isRunning: () => ideRef.current?.isRunning() ?? false,
    setInputs: (inputs: string[]) => ideRef.current?.setInputs(inputs),
  }));

  useEffect(() => {
    ideRef.current?.setLanguage(languageMode);
  }, [languageMode, ideRef]);

  // Stop any in-flight code streaming when the world changes or on unmount.
  useEffect(() => cancelStream, [world]);

  return (
    <div className={styles.ide}>
      <div className={styles.editorPane}>
        <CodeMirrorEditor ref={editorHandleRef} readOnly={readOnly} />
      </div>
      <div className={styles.canvasPane}>
        {panelHeader && <div className={styles.panelHeader}>{panelHeader}</div>}
        <div ref={canvasBodyRef} className={styles.canvasBody}>
          <canvas ref={canvasRef} className={styles.canvas} />
          {!canvasReady && (
            <div className={styles.canvasLoading} aria-hidden="true" />
          )}
          {panelOverlay && (
            <div className={styles.panelOverlay}>{panelOverlay}</div>
          )}
        </div>
      </div>
    </div>
  );
});
